import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SECURITY: Token Storage Utilities
 * 
 * These functions provide protection for localStorage tokens:
 * - Obfuscation against casual inspection
 * - Integrity validation (basic JWT structure check)
 * - Automatic expiry handling
 * - Migration support from legacy plain tokens
 * 
 * For full security, migrate to httpOnly cookies (requires backend changes).
 */
const TOKEN_KEY = 'la_tk';
const REFRESH_KEY = 'la_rt';
const TOKEN_EXPIRY_KEY = 'la_exp';
const INTEGRITY_KEY = 'la_int';

// Validate JWT structure (header.payload.signature)
const isValidJWTStructure = (token) => {
  if (!token || typeof token !== 'string') return false;
  const parts = token.split('.');
  if (parts.length !== 3) return false;
  try {
    // Verify each part is valid base64url
    parts.forEach(part => {
      const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
      atob(base64);
    });
    return true;
  } catch {
    return false;
  }
};

// Generate simple integrity hash for token verification
const generateIntegrity = (token) => {
  if (!token) return null;
  // Simple checksum based on token length and character codes
  let hash = 0;
  for (let i = 0; i < token.length; i++) {
    hash = ((hash << 5) - hash) + token.charCodeAt(i);
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
};

const encodeToken = (token) => {
  if (!token) return null;
  try {
    // Simple obfuscation - NOT encryption
    return btoa(encodeURIComponent(token).split('').reverse().join(''));
  } catch {
    return token;
  }
};

const decodeToken = (encoded) => {
  if (!encoded) return null;
  try {
    return decodeURIComponent(atob(encoded).split('').reverse().join(''));
  } catch {
    // If decoding fails, assume it's already plain (migration support)
    return encoded;
  }
};

const storeToken = (token, isRefresh = false) => {
  const key = isRefresh ? REFRESH_KEY : TOKEN_KEY;
  if (token) {
    // Validate token structure before storing
    if (!isValidJWTStructure(token)) {
      console.warn('[Auth] Invalid token structure, not storing');
      return;
    }
    
    localStorage.setItem(key, encodeToken(token));
    
    // Store expiry and integrity for access token (24h from now)
    if (!isRefresh) {
      const expiry = Date.now() + (24 * 60 * 60 * 1000);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiry.toString());
      localStorage.setItem(INTEGRITY_KEY, generateIntegrity(token));
    }
  } else {
    localStorage.removeItem(key);
    if (!isRefresh) {
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      localStorage.removeItem(INTEGRITY_KEY);
    }
  }
};

const getToken = (isRefresh = false) => {
  const key = isRefresh ? REFRESH_KEY : TOKEN_KEY;
  const encoded = localStorage.getItem(key);
  
  // Check expiry for access token
  if (!isRefresh) {
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (expiry && Date.now() > parseInt(expiry, 10)) {
      // Token expired, clear it
      localStorage.removeItem(key);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      localStorage.removeItem(INTEGRITY_KEY);
      return null;
    }
  }
  
  // Try to decode, fall back to plain if migration needed
  const decoded = decodeToken(encoded);
  
  // Validate token structure
  if (decoded && !isValidJWTStructure(decoded)) {
    // Invalid token structure, clear it
    localStorage.removeItem(key);
    if (!isRefresh) {
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      localStorage.removeItem(INTEGRITY_KEY);
    }
    return null;
  }
  
  // Verify integrity for access token
  if (!isRefresh && decoded) {
    const storedIntegrity = localStorage.getItem(INTEGRITY_KEY);
    const currentIntegrity = generateIntegrity(decoded);
    if (storedIntegrity && storedIntegrity !== currentIntegrity) {
      // Token may have been tampered with
      console.warn('[Auth] Token integrity check failed');
      localStorage.removeItem(key);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      localStorage.removeItem(INTEGRITY_KEY);
      return null;
    }
  }
  
  // Migrate plain tokens to encoded format
  if (encoded && decoded === encoded && encoded.includes('.')) {
    // Looks like a plain JWT, re-encode it
    storeToken(encoded, isRefresh);
    return encoded;
  }
  
  return decoded;
};

const clearTokens = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  localStorage.removeItem(INTEGRITY_KEY);
  // Also clear legacy keys for migration
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  
  // SECURITY: Clear ALL wizard/funnel cached data to prevent cross-user data leaks
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (
      key.startsWith('wizard_global_state') ||
      key.startsWith('ai_history_') ||
      key.startsWith('wizard_') ||
      key.startsWith('funnel_') ||
      key.startsWith('deploy_states') ||
      key.startsWith('integration_') ||
      key.startsWith('creativos_')
    )) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Use secure token getter with migration support
  const [token, setToken] = useState(() => getToken() || localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);
  const isRefreshingRef = useRef(false);

  // Instancia de axios con timeout configurado
  const axiosInstance = axios.create({
    baseURL: API_URL,
    timeout: 180000, // 3 minutes for general requests
  });

  // Instancia especial para uploads grandes (videos)
  const uploadInstance = axios.create({
    baseURL: API_URL,
    timeout: 300000, // 5 minutes for uploads
    maxContentLength: 100 * 1024 * 1024, // 100MB
    maxBodyLength: 100 * 1024 * 1024,
  });

  // Interceptor para añadir token a cada request
  axiosInstance.interceptors.request.use((config) => {
    const currentToken = getToken() || localStorage.getItem('token');
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    return config;
  });

  // Same interceptor for upload instance
  uploadInstance.interceptors.request.use((config) => {
    const currentToken = getToken() || localStorage.getItem('token');
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    return config;
  });

  // Logout limpio
  const performLogout = useCallback(() => {
    clearTokens();
    setToken(null);
    setUser(null);
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
  }, []);

  // Función de refresh
  const performRefresh = useCallback(async () => {
    if (isRefreshingRef.current) return null;
    
    const refreshToken = getToken(true) || localStorage.getItem('refresh_token');
    if (!refreshToken) {
      performLogout();
      return null;
    }
    
    isRefreshingRef.current = true;
    
    try {
      const response = await axios.post(`${API_URL}/auth/refresh`, {
        refresh_token: refreshToken
      });
      
      const { access_token, refresh_token: newRefreshToken } = response.data;
      
      // Store tokens securely
      storeToken(access_token, false);
      storeToken(newRefreshToken, true);
      setToken(access_token);
      
      return access_token;
    } catch (error) {
      performLogout();
      return null;
    } finally {
      isRefreshingRef.current = false;
    }
  }, [performLogout]);

  // Programa refresh automático antes de que expire
  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    // Refresca a las 23 horas (1 hora antes de expirar)
    const REFRESH_INTERVAL = 23 * 60 * 60 * 1000;
    refreshTimerRef.current = setTimeout(() => {
      performRefresh().then(() => {
        scheduleRefresh(); // Re-schedule after refresh
      });
    }, REFRESH_INTERVAL);
  }, [performRefresh]);

  // Interceptor para manejar token expirado automáticamente
  useEffect(() => {
    const responseInterceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // Si es 401 y no es el endpoint de refresh/login
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url?.includes('/auth/refresh') &&
          !originalRequest.url?.includes('/auth/login')
        ) {
          originalRequest._retry = true;
          
          try {
            const newToken = await performRefresh();
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return axiosInstance(originalRequest);
            }
          } catch (refreshError) {
            // Refresh falló — logout
            performLogout();
            return Promise.reject(refreshError);
          }
        }
        
        return Promise.reject(error);
      }
    );

    return () => {
      axiosInstance.interceptors.response.eject(responseInterceptor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [performRefresh, performLogout]);

  // Inicialización — verifica token al montar
  useEffect(() => {
    const initAuth = async () => {
      // Use secure getters with migration support
      const savedToken = getToken() || localStorage.getItem('token');
      const savedRefreshToken = getToken(true) || localStorage.getItem('refresh_token');
      
      if (savedToken) {
        try {
          const response = await axiosInstance.get('/auth/me');
          setUser(response.data);
          setToken(savedToken);
          // Migrate plain tokens to secure storage
          if (localStorage.getItem('token')) {
            storeToken(savedToken, false);
          }
          if (localStorage.getItem('refresh_token')) {
            storeToken(savedRefreshToken, true);
          }
          scheduleRefresh();
        } catch (error) {
          // Token principal expirado — intenta refresh
          if (savedRefreshToken) {
            const newToken = await performRefresh();
            if (newToken) {
              try {
                const response = await axiosInstance.get('/auth/me');
                setUser(response.data);
                scheduleRefresh();
              } catch {
                performLogout();
              }
            }
          } else {
            performLogout();
          }
        }
      }
      setLoading(false);
    };
    
    initAuth();
    
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const response = await axiosInstance.post('/auth/login', { email, password });
    const { access_token, refresh_token, user: userData } = response.data;
    
    // Store tokens securely
    storeToken(access_token, false);
    storeToken(refresh_token, true);
    setToken(access_token);
    setUser(userData);
    scheduleRefresh();
    
    return userData;
  };

  const register = async (name, email, password, agencyName) => {
    const response = await axiosInstance.post('/auth/register', {
      name,
      email,
      password,
      agency_name: agencyName,
    });
    const { access_token, refresh_token, user: userData } = response.data;
    
    // Store tokens securely
    storeToken(access_token, false);
    storeToken(refresh_token, true);
    setToken(access_token);
    setUser(userData);
    scheduleRefresh();
    
    return userData;
  };

  const logout = () => {
    performLogout();
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    api: axiosInstance,
    uploadApi: uploadInstance, // Special API for large file uploads
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
